/*
  Splits the product description into an open intro plus collapsible sections.

  Done in the DOM rather than in Liquid on purpose. The description is ShineOn `body_html` and its
  markup is not consistent across the catalogue — sampled two products and found <h3> vs <h4> for the
  same section, "Product Specification" vs "Product specifications:", and a "Great for:" block on one
  but not the other. Any Liquid split on literal strings would silently fail on some of the 123
  published products; walking rendered elements handles all of the variants.

  Rules:
    - everything before the first heading stays open as the main description
    - a "Product Description" heading is dropped and its content stays in the open intro
    - a paragraph opening with "Great for:" becomes a "Use Case" section
    - any spec heading becomes "Product Specification"
    - any other heading keeps its own text as the section title
*/
(function () {
  const root = document.querySelector('.product__description');
  if (!root || root.dataset.accordionised === 'true') return;

  const HEADING = /^H[2-4]$/;

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const ICONS = {
    'Product Specification':
      '<path d="M6.2 3h11.6l3.7 6-9.5 12L2.5 9z"/><path d="M2.5 9h19M9 3l3 6 3-6M12 9v12"/>',
    'Use Case':
      '<rect x="3" y="8.5" width="18" height="12.5" rx="1.5"/><path d="M3 13h18M12 8.5V21"/>'
      + '<path d="M12 8.5S9.6 3.9 7.2 4.6c-1.5.4-1.9 2.3-.7 3.2.8.6 5.5.7 5.5.7z"/>'
      + '<path d="M12 8.5s2.4-4.6 4.8-3.9c1.5.4 1.9 2.3.7 3.2-.8.6-5.5.7-5.5.7z"/>',
    _card:
      '<rect x="2.5" y="5" width="19" height="14" rx="2"/><path d="M6.5 9.5h6M6.5 13h9"/>',
    _default:
      '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/>'
      + '<path d="M14 3v5h5M8.5 13h7M8.5 16.5h5"/>',
  };

  // Wrapped in the same .jewe-acc-ico span the authored headings use, so one CSS rule governs
  // spacing and alignment for all six rows instead of two treatments that drift apart.
  const iconFor = (title) => {
    let d = ICONS[title];
    if (!d) d = /card|message/i.test(title) ? ICONS._card : ICONS._default;
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.6');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('focusable', 'false');
    svg.innerHTML = d;
    const span = document.createElement('span');
    span.className = 'jewe-acc-ico';
    span.setAttribute('aria-hidden', 'true');
    span.appendChild(svg);
    return span;
  };

  const titleFor = (raw) => {
    const t = raw.replace(/\s+/g, ' ').replace(/[:：]\s*$/, '').trim();
    if (/product\s+specification/i.test(t)) return 'Product Specification';
    if (/great\s+for/i.test(t)) return 'Use Case';
    return t;
  };

  /*
    Some products use no headings at all — sampled one where every section label is just a bold
    paragraph, e.g. <p><strong>Product specifications:</strong></p>. Those have to count as headings
    too or the whole description stays open. A trailing colon is the signal, plus an allow-list for
    the known labels that omit it.
  */
  const KNOWN = /^(great\s+for|product\s+specifications?|message\s+on\s+the\s+card|the\s+message\s+card\s+reads)$/i;

  const labelInfo = (el) => {
    if (!/^(P|DIV)$/.test(el.tagName)) return null;
    const strong = el.querySelector('strong, b');
    if (!strong) return null;
    const full = el.textContent.replace(/\s+/g, ' ').trim();
    const label = strong.textContent.replace(/\s+/g, ' ').trim();
    if (!label || !full.startsWith(label)) return null;

    const bare = label.replace(/[:：]\s*$/, '').trim();
    if (!/[:：]\s*$/.test(label) && !KNOWN.test(bare)) return null;

    const rest = full.slice(label.length).replace(/^[:：\s]+/, '').trim();
    return { strong, label: bare, hasInlineBody: rest.length > 0 };
  };

  const intro = [];
  const trailing = [];
  const groups = [];
  let current = null;

  Array.from(root.children).forEach((node) => {
    if (HEADING.test(node.tagName)) {
      const text = node.textContent.replace(/\s+/g, ' ').trim();
      if (/^product\s+description\s*[:：]?$/i.test(text)) {
        // Redundant label above the intro — drop it, keep what follows open.
        current = null;
        return;
      }
      current = { title: titleFor(text), nodes: [] };
      groups.push(current);
      return;
    }

    const info = labelInfo(node);
    if (info) {
      info.strong.remove();
      // The label is usually followed by a <br> that would leave a blank first line.
      while (node.firstChild && node.firstChild.nodeName === 'BR') node.firstChild.remove();
      current = { title: titleFor(info.label), nodes: [] };
      groups.push(current);
      // A label with body text on the same line keeps that text; a bare label takes what follows.
      if (info.hasInlineBody) current.nodes.push(node);
      return;
    }

    /*
      A spec section is a list. Without this bound it swallows whatever prose follows it, because
      sections otherwise run until the next label — measured on one product where a closing line
      ("You are not on her birth certificate...") ended up filed under Product Specification.
      The first non-list block closes the section and drops into the trailing bucket instead.
    */
    if (current && current.title === 'Product Specification' && current.nodes.length) {
      if (!/^(UL|OL)$/.test(node.tagName)) {
        current = null;
        trailing.push(node);
        return;
      }
    }

    (current ? current.nodes : intro).push(node);
  });

  if (!groups.length) return;

  /*
    Two fragments. The prose stays inside .product__description; the generated rows are inserted
    after it as siblings of the theme's own accordions. Left inside the description they were not
    siblings, so Dawn's "adjacent accordion loses its top border" rule never applied and the seam
    between the two sets showed a doubled hairline with 57px of dead space between them.
  */
  const frag = document.createDocumentFragment();
  const rows = document.createDocumentFragment();
  intro.forEach((n) => frag.appendChild(n));

  groups.forEach((g) => {
    if (!g.nodes.length) return;
    const wrap = document.createElement('div');
    wrap.className = 'product__accordion accordion quick-add-hidden';

    const details = document.createElement('details');
    const summary = document.createElement('summary');
    const titleWrap = document.createElement('div');
    titleWrap.className = 'summary__title';
    const h = document.createElement('h2');
    h.className = 'h4 accordion__title';
    h.appendChild(iconFor(g.title));
    h.appendChild(document.createTextNode(' ' + g.title));
    titleWrap.appendChild(h);
    summary.appendChild(titleWrap);

    // Reuse the theme's caret so these match the hand-authored rows beneath them.
    const caret = document.querySelector('.product__accordion summary .icon-caret');
    if (caret) summary.appendChild(caret.cloneNode(true));

    const content = document.createElement('div');
    content.className = 'accordion__content rte';
    g.nodes.forEach((n) => content.appendChild(n));

    details.appendChild(summary);
    details.appendChild(content);
    wrap.appendChild(details);
    rows.appendChild(wrap);
  });

  // Closing prose stays open below the sections rather than buried inside one of them.
  trailing.forEach((n) => frag.appendChild(n));

  root.innerHTML = '';
  root.appendChild(frag);
  if (root.parentNode) root.parentNode.insertBefore(rows, root.nextSibling);
  root.dataset.accordionised = 'true';
})();
